import moment from "moment";

const DISPLAY_DATE_FORMAT = "DD-MM-YYYY";
// Matched against the backend's own vocabulary, singular forms included. An unrecognised format is not
// guessed at, for the same reason the backend refuses to guess: it means we do not know when the course
// ended, which is not the same as knowing it has.
const DURATION_UNIT_BY_FORMAT = {
  day: "days",
  days: "days",
  month: "months",
  months: "months",
};

/**
 * The last day of a course, derived from when it starts and how long it runs.
 *
 * Both prescription writers used to default this to today, which stored a course that finished on the
 * day it was issued. That was harmless while `finishDate` meant nothing; since KIN-1928 the backend
 * reads a last day before today as *discontinued*, so the default quietly moved long-term scripts into
 * Discontinued — including in what we send a maternity hospital (KIN-1932).
 *
 * Returns an empty string when there is no duration to derive from, rather than falling back to a date.
 * An absent finish date is sent as null and means "no end recorded", which is the honest answer; a
 * guessed one is indistinguishable from a prescriber's deliberate choice.
 *
 * The arithmetic matches the backend's own fallback exactly — `startDate.plusDays(duration).minusDays(1)`
 * in `PatientMedicationHistoryService.courseLastDay` (KIN-1928): a course of n units beginning on its
 * start date runs through the day before start + n, so the start day counts. The two must agree, because
 * an explicit finishDate wins over the duration backend-side: a date derived differently here would
 * silently override the backend's own answer by a day, and a day is the difference between a course
 * reading as current and reading as discontinued.
 */
export const deriveFinishDate = ({ startDate, duration, durationFormat }) => {
  const start = moment(startDate, DISPLAY_DATE_FORMAT, true);
  const length = Number(duration);

  if (!start.isValid() || !Number.isFinite(length) || length <= 0) {
    return "";
  }

  const unit = DURATION_UNIT_BY_FORMAT[durationFormat?.trim().toLowerCase()];

  if (!unit) {
    return "";
  }

  return start.add(length, unit).subtract(1, "days").format(DISPLAY_DATE_FORMAT);
};
